/**
 * Compact multi-line trend strip for the Companion Body screen.
 *
 * Pulls the patient's last `limit` measurement logs and renders one line
 * per non-empty series (waist / hip / chest / arms / thighs). Optimised
 * to surface waist/hip/chest movement *before* the scale moves, which
 * is the typical GLP-1 response pattern and a strong retention signal.
 *
 * Auto-hides if the patient hasn't logged ≥2 weeks of any single metric.
 */
import { useEffect, useState } from "react";
import API from "@/lib/api";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { Ruler } from "lucide-react";

const METRIC_DEFS = [
  { key: "waist_in", label: "Waist", color: "#7c3aed" },
  { key: "hip_in",   label: "Hips",  color: "#ec4899" },
  { key: "chest_in", label: "Chest", color: "#0891b2" },
  { key: "left_arm_in",   label: "L arm", color: "#059669" },
  { key: "right_arm_in",  label: "R arm", color: "#d97706" },
  { key: "left_thigh_in", label: "L thigh", color: "#0ea5e9" },
  { key: "right_thigh_in", label: "R thigh", color: "#f43f5e" },
];

function fmtTick(iso) {
  try {
    const d = new Date(iso);
    return `${d.getMonth() + 1}/${d.getDate()}`;
  } catch { return ""; }
}

export default function MeasurementTrendStrip({ limit = 8 }) {
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    API.get(`/wellness/measurements/series?limit=${limit * 4}`)
      .then(({ data }) => {
        if (cancelled) return;
        const series = Array.isArray(data?.series) ? data.series : [];
        // Oldest → newest so the line reads left-to-right
        setRows(series.slice().reverse());
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, [limit]);

  if (!loaded) return null;

  // Detect which metrics have ≥2 data points; auto-skip the chart entirely
  // if none qualify — the strip stays out of the way for new patients.
  const activeMetrics = METRIC_DEFS.filter((m) =>
    rows.reduce((n, r) => (typeof r?.[m.key] === "number" ? n + 1 : n), 0) >= 2
  );
  if (activeMetrics.length === 0) {
    return (
      <div className="rounded-2xl bg-slate-50 dark:bg-slate-900/40 border border-dashed border-slate-300 dark:border-slate-700 p-4 text-center" data-testid="companion-measurement-strip-empty">
        <Ruler className="w-4 h-4 text-slate-400 mx-auto mb-1.5" />
        <p className="text-xs text-slate-500 leading-snug">
          Log measurements 2+ weeks in a row and a trend strip will appear here. Waist &amp; hips often shrink before the scale moves.
        </p>
      </div>
    );
  }

  // Build a row per log entry. recharts dataKey reads each metric key
  // directly — missing keys are skipped via `connectNulls`.
  const chartData = rows.map((r) => ({
    date: fmtTick(r.logged_at),
    ...Object.fromEntries(activeMetrics.map((m) => [m.key, r?.[m.key] ?? null])),
  }));

  // Y-axis padding so the lines aren't pinned to the edges.
  let minV = Infinity, maxV = -Infinity;
  for (const r of rows) {
    for (const m of activeMetrics) {
      const v = r?.[m.key];
      if (typeof v === "number") { minV = Math.min(minV, v); maxV = Math.max(maxV, v); }
    }
  }
  if (!Number.isFinite(minV)) { minV = 0; maxV = 1; }

  return (
    <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-3" data-testid="companion-measurement-strip">
      <div className="flex items-center justify-between px-1 mb-1">
        <div className="flex items-center gap-1.5">
          <Ruler className="w-3.5 h-3.5 text-purple-600" />
          <p className="text-xs font-bold text-slate-900 dark:text-white">Measurement trend</p>
        </div>
        <span className="text-[10px] text-slate-400">last {Math.min(rows.length, limit)} weeks · inches</span>
      </div>
      <div className="h-32 -mx-1">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis dataKey="date" tick={{ fontSize: 9, fill: "#64748b" }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
            <YAxis domain={[Math.floor(minV - 0.5), Math.ceil(maxV + 0.5)]} tick={{ fontSize: 9, fill: "#64748b" }} axisLine={false} tickLine={false} width={28} />
            <Tooltip contentStyle={{ fontSize: 10, borderRadius: 8, border: "1px solid #e2e8f0" }} />
            <Legend wrapperStyle={{ fontSize: 10 }} iconSize={8} />
            {activeMetrics.map((m) => (
              <Line
                key={m.key}
                type="monotone"
                dataKey={m.key}
                name={m.label}
                stroke={m.color}
                strokeWidth={1.8}
                dot={{ r: 2, fill: m.color, stroke: "#fff", strokeWidth: 1 }}
                connectNulls
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      {rows.length >= 2 && (
        <p className="text-[10px] text-slate-400 px-1 mt-0.5 leading-snug">
          Waist &amp; hips usually shrink before the scale moves — keep going!
        </p>
      )}
    </div>
  );
}
